import { useNavigate } from "react-router-dom";
import { ChevronRight, Crown } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Stepper } from "@/components/Stepper";
import { StatusBadge } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";

export default function Dashboard() {
  const navigate = useNavigate();
  const { member } = useAuth();
  if (!member) return null;

  const nextStage = member.stages.find((s) => s.key === member.next_step_key);
  const canAdvance = member.next_step_route && member.next_step_route !== "/dashboard";

  return (
    <Layout>
      {/* Member identity card */}
      <div className="rounded-2xl border border-slate-800 bg-[#0D131C]/90 p-6 shadow-2xl shadow-black/60 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-40 h-40 bg-emerald-500/5 blur-3xl rounded-full" />
        <div className="flex items-start justify-between relative">
          <div>
            <p className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-slate-500">Member</p>
            <p
              data-testid="member-number-display"
              className="font-serif-display text-3xl sm:text-4xl text-slate-50 mt-1 tracking-wide leading-tight"
            >
              {member.profile?.full_name || member.name}
            </p>
          </div>
          {member.is_pioneer && (
            <div className="flex flex-col items-end gap-1">
              <div className="flex items-center gap-1.5 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-3 py-1">
                <Crown className="w-3 h-3 text-[#D4AF37]" />
                <span className="font-mono-ui text-[9px] uppercase tracking-[0.2em] text-[#D4AF37]">Pioneer</span>
              </div>
            </div>
          )}
        </div>

        <div className="mt-5 pt-5 border-t border-slate-800/70 flex items-center justify-between">
          <span className="font-mono-ui text-[10px] uppercase tracking-[0.2em] text-slate-500">Overall Status</span>
          <StatusBadge state="overall" label={member.overall_status} testId="member-status-badge" />
        </div>
      </div>

      {/* Next step */}
      {canAdvance && (
        <button
          onClick={() => navigate(member.next_step_route)}
          data-testid="next-step-button"
          className="mt-4 w-full text-left rounded-2xl border border-emerald-600/40 bg-emerald-950/25 p-5 flex items-center justify-between hover:bg-emerald-950/40 transition-colors group"
        >
          <div>
            <p className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-emerald-400/80">Next Required Step</p>
            <p className="text-lg text-slate-100 mt-1 font-medium">{nextStage?.label}</p>
          </div>
          <ChevronRight className="w-5 h-5 text-emerald-400 group-hover:translate-x-1 transition-transform" />
        </button>
      )}

      {!canAdvance && (
        <div className="mt-4 rounded-2xl border border-slate-800 bg-[#0D131C]/90 p-5">
          <p className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-emerald-400/80">Under Review</p>
          <p className="text-sm text-slate-300 mt-2 leading-relaxed">
            Your file is being prepared for transfer to the approved regulated partner onboarding pathway. You will be
            notified when the next action is required.
          </p>
        </div>
      )}

      {/* Compliance tracker */}
      <div className="mt-6 rounded-2xl border border-slate-800 bg-[#0D131C]/90 p-6">
        <div className="flex items-center gap-2 mb-6">
          <h2 className="font-serif-display text-xl text-slate-100 tracking-tight">Compliance Status</h2>
          <span className="text-emerald-500/60">◆</span>
          <span className="font-mono-ui text-[9px] uppercase tracking-[0.2em] text-slate-500">Live Tracker</span>
        </div>
        <Stepper stages={member.stages} />
      </div>
    </Layout>
  );
}
